import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    Box,
    Grid,
    Chip,
    Divider,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    IconButton,
    TextField,
    Button,
    Collapse,
    ImageList,
    ImageListItem,
} from '@mui/material';
import {
    AccessTime as AccessTimeIcon,
    AttachMoney as AttachMoneyIcon,
    LocationOn as LocationOnIcon,
    Group as GroupIcon,
    Info as InfoIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Close as CloseIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    ArrowBack as ArrowBackIcon,
    AddPhotoAlternate as AddPhotoAlternateIcon,
    Delete as DeleteIcon,
    Add as AddIcon
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import TourDatesManager from '../components/TourDatesManager';
import Navbar from '../components/Navbar';
import useTranslation from '../hooks/useTranslation';
import * as XLSX from 'xlsx';

const MAX_IMAGES = 5;
const MAX_LINES = 3;

const TourManagementPage = () => {
    const { t } = useTranslation();
    const { tourId } = useParams();
    const history = useHistory();
    const [tour, setTour] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [editedTour, setEditedTour] = useState(null);
    const [images, setImages] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});
    const [expandedFields, setExpandedFields] = useState({
        description: false,
        included: {},
        notIncluded: {},
        tips: {}
    });

    useEffect(() => {
        fetchTourDetails();
    }, [tourId]);

    const fetchTourDetails = async () => {
        try {
            const response = await axios.get(`/operators/tours/${tourId}`);
            const tourData = response.data;
            
            // Parse JSON strings if they exist
            try {
                tourData.included = tourData.included ? JSON.parse(tourData.included) : [];
                tourData.notIncluded = tourData.notIncluded ? JSON.parse(tourData.notIncluded) : [];
                tourData.tips = tourData.tips ? JSON.parse(tourData.tips) : [];
                const parsedImages = tourData.image_url ? JSON.parse(tourData.image_url) : [];
                setImages(Array.isArray(parsedImages) ? parsedImages : []);
            } catch (e) {
                console.error('Error parsing JSON fields:', e);
                tourData.included = tourData.included ? [tourData.included] : [];
                tourData.notIncluded = tourData.notIncluded ? [tourData.notIncluded] : [];
                tourData.tips = tourData.tips ? [tourData.tips] : [];
                setImages([]);
            }

            setTour(tourData);
            setEditedTour(tourData);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching tour details:', error);
            setLoading(false);
            toast.error(t('pages.tours.operator.tourManagement.notifications.loadError'));
        }
    };

    const handleImageUpload = async (event) => {
        const files = Array.from(event.target.files);
        
        if (images.length + files.length > MAX_IMAGES) {
            toast.error(t('pages.tours.operator.tourForm.notifications.maxImagesError', { max: MAX_IMAGES }));
            return;
        }

        // Validate file sizes (5MB = 5 * 1024 * 1024 bytes)
        const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
        const oversizedFiles = files.filter(file => file.size > MAX_FILE_SIZE);
        
        if (oversizedFiles.length > 0) {
            const fileNames = oversizedFiles.map(file => file.name).join(', ');
            toast.error(t('pages.tours.operator.tourForm.notifications.imageSizeError'));
            return;
        }

        setIsUploading(true);
        const uploadPromises = files.map(async (file) => {
            const formData = new FormData();
            formData.append('image', file);

            try {
                const response = await axios.post('/operators/tours/image', formData, {
                    onUploadProgress: (progressEvent) => {
                        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setUploadProgress(prev => ({
                            ...prev,
                            [file.name]: progress
                        }));
                    }
                });

                return response.data.imageUrl;
            } catch (error) {
                console.error('Error uploading image:', error);
                toast.error(t('pages.tours.operator.tourForm.notifications.imageUploadError'));
                return null;
            }
        });

        try {
            const uploadedUrls = await Promise.all(uploadPromises);
            const validUrls = uploadedUrls.filter(url => url !== null);
            const newImages = [...images, ...validUrls];
            
            setImages(newImages);
            
            // Update the tour with new images
            const updatedTour = {
                ...tour,
                image_url: JSON.stringify(newImages),
                included: tour.included,
                notIncluded: tour.notIncluded,
                tips: tour.tips
            };
            
            await axios.put(`/operators/tours/${tourId}`, updatedTour);
            toast.success(t('pages.tours.operator.tourForm.notifications.imageUploadSuccess'));
        } catch (error) {
            console.error('Error processing uploads:', error);
            toast.error(t('pages.tours.operator.tourForm.notifications.imageUploadError'));
        } finally {
            setIsUploading(false);
            setUploadProgress({});
        }
    };

    const handleRemoveImage = async (indexToRemove) => {
        try {
            const newImages = images.filter((_, index) => index !== indexToRemove);
            
            // Update the tour with the new images array
            await axios.put(`/operators/tours/${tourId}`, {
                ...tour,
                image_url: JSON.stringify(newImages)
            });
            
            setImages(newImages);
            toast.success(t('pages.tours.operator.tourForm.notifications.imageRemoveSuccess'));
        } catch (error) {
            console.error('Error removing image:', error);
            toast.error(t('pages.tours.operator.tourForm.notifications.imageUploadError'));
        }
    };

    const handleSave = async () => {
        try {
            const updatedTour = {
                ...editedTour,
                included: JSON.stringify(editedTour.included),
                notIncluded: JSON.stringify(editedTour.notIncluded),
                tips: JSON.stringify(editedTour.tips),
                image_url: JSON.stringify(images)
            };
            await axios.put(`/operators/tours/${tourId}`, updatedTour);
            setTour(editedTour);
            setIsEditing(false);
            fetchTourDetails();
        } catch (error) {
            console.error('Error updating tour:', error);
        }
    };

    const handleCancel = () => {
        setEditedTour(tour);
        setIsEditing(false);
    };

    const downloadParticipantsList = async () => {
        try {
            // Fetch participants data
            const response = await axios.get(`/operators/tours/${tourId}/participants`);
            const participants = response.data;

            // Format data for Excel
            const worksheetData = [
                ['Name', 'Email', 'Phone', 'Booking Date', 'Number of Tickets'], // Headers
                ...participants.map(p => [
                    p.name,
                    p.email,
                    p.phone,
                    new Date(p.bookingDate).toLocaleDateString(),
                    p.numberOfTickets
                ])
            ];

            // Create worksheet
            const ws = XLSX.utils.aoa_to_sheet(worksheetData);

            // Create workbook
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Participants');

            // Generate filename with tour name and date
            const fileName = `${tour.name}_participants_${new Date().toISOString().split('T')[0]}.xlsx`;

            // Download file
            XLSX.writeFile(wb, fileName);
            
            toast.success('Participants list downloaded successfully');
        } catch (error) {
            console.error('Error downloading participants list:', error);
            toast.error('Failed to download participants list');
        }
    };

    const handleToggleExpand = (field, index) => {
        if (typeof index === 'number') {
            setExpandedFields(prev => ({
                ...prev,
                [field]: {
                    ...prev[field],
                    [index]: !prev[field][index]
                }
            }));
        } else {
            setExpandedFields(prev => ({
                ...prev,
                [field]: !prev[field]
            }));
        }
    };

    const renderExpandableText = (text, field, index) => {
        if (!text) return null;
        
        const lines = text.split('\n').filter(line => line.trim() !== '');
        const isExpanded = typeof index === 'number' 
            ? expandedFields[field][index] 
            : expandedFields[field];
        const shouldShowMore = lines.length > MAX_LINES || text.length > 150;

        return (
            <Box>
                <Typography 
                    variant="body1" 
                    sx={{ 
                        whiteSpace: 'pre-line',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: !isExpanded ? '-webkit-box' : 'block',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: !isExpanded ? MAX_LINES : undefined,
                        mb: shouldShowMore ? 0.5 : 0,
                        lineHeight: 1.5
                    }}
                >
                    {text}
                </Typography>
                {shouldShowMore && (
                    <Button 
                        onClick={() => handleToggleExpand(field, index)}
                        sx={{ 
                            color: '#316C6E',
                            p: 0,
                            minWidth: 'auto',
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            display: 'block',
                            '&:hover': {
                                background: 'none',
                                textDecoration: 'underline'
                            }
                        }}
                    >
                        {isExpanded ? t('pages.tourModal.showLess') : t('pages.tourModal.showMore')}
                    </Button>
                )}
            </Box>
        );
    };

    const handleAddItem = (field) => {
        setEditedTour(prev => ({
            ...prev,
            [field]: [...(prev[field] || []), '']
        }));
    };

    const handleRemoveItem = (field, index) => {
        setEditedTour(prev => ({
            ...prev,
            [field]: prev[field].filter((_, i) => i !== index)
        }));
    };

    if (loading) {
        return (
            <Box sx={{ bgcolor: '#f5f7f9', minHeight: '100vh' }}>
                <Navbar hideFilters sx={{ bgcolor: '#f5f7f9' }} />
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                    <CircularProgress />
                </Box>
            </Box>
        );
    }

    if (!tour) {
        return (
            <Box sx={{ bgcolor: '#f5f7f9', minHeight: '100vh' }}>
                <Navbar hideFilters sx={{ bgcolor: '#f5f7f9' }} />
                <Container>
                    <Typography variant="h5" color="error" sx={{ mt: 4 }}>
                        {t('pages.tours.operator.tourManagement.tourNotFound')}
                    </Typography>
                </Container>
            </Box>
        );
    }

    return (
        <Box sx={{ bgcolor: '#f5f7f9', minHeight: '100vh' }}>
            <Navbar hideFilters sx={{ bgcolor: '#f5f7f9' }} />
            <Box sx={{ py: 4 }}>
                <Container maxWidth="lg">
                    <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => history.push('/operator/dashboard')}
                            sx={{ color: '#316C6E' }}
                        >
                            {t('pages.tours.operator.tourManagement.backToDashboard')}
                        </Button>
                    </Box>

                    <Grid container spacing={3}>
                        {/* Tour Details Section */}
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 3, height: '100%', bgcolor: '#fff' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                    <Box sx={{ flex: 1, mr: 2 }}>
                                        {isEditing ? (
                                            <>
                                                <TextField
                                                    value={editedTour.name}
                                                    onChange={(e) => setEditedTour({ ...editedTour, name: e.target.value })}
                                                    fullWidth
                                                    variant="standard"
                                                />
                                                <TextField
                                                    value={editedTour.price || ''}
                                                    onChange={(e) => setEditedTour({ ...editedTour, price: e.target.value })}
                                                    fullWidth
                                                    variant="standard"
                                                    type="number"
                                                    sx={{ mt: 1 }}
                                                />
                                            </>
                                        ) : (
                                            <>
                                                <Typography variant="h5" sx={{ color: '#316C6E', fontWeight: 'bold' }}>
                                                    {tour.name}
                                                </Typography>
                                                <Typography variant="body1" sx={{ color: '#666', mt: 0.5 }}>
                                                    {new Intl.NumberFormat('en-US', {
                                                        style: 'currency',
                                                        currency: 'DOP',
                                                        minimumFractionDigits: 0,
                                                        maximumFractionDigits: 0
                                                    }).format(tour.price || 0)}
                                                </Typography>
                                            </>
                                        )}
                                    </Box>
                                    <IconButton 
                                        onClick={() => setIsEditing(true)} 
                                        sx={{ 
                                            color: '#316C6E',
                                            padding: '4px',
                                            visibility: isEditing ? 'hidden' : 'visible'
                                        }}
                                    >
                                        <EditIcon sx={{ fontSize: '1.2rem' }} />
                                    </IconButton>
                                </Box>

                                {/* Images Section */}
                                <Box sx={{ mb: 3 }}>
                                    <Box sx={{ mb: 2 }}>
                                        <Button
                                            component="label"
                                            variant="outlined"
                                            startIcon={<AddPhotoAlternateIcon />}
                                            disabled={isUploading || images.length >= MAX_IMAGES}
                                            sx={{ mr: 2 }}
                                        >
                                            {t('pages.tours.operator.tourForm.uploadImages')}
                                            <input
                                                type="file"
                                                hidden
                                                multiple
                                                accept="image/*"
                                                onChange={handleImageUpload}
                                            />
                                        </Button>
                                                                <Box>
                            <Typography variant="caption" color="textSecondary">
                                {images.length}/{MAX_IMAGES} {t('pages.tours.operator.tourForm.images')}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                                {t('pages.tours.operator.tourForm.maxImageSize')}
                            </Typography>
                        </Box>
                                    </Box>
                                    
                                    {images.length > 0 && (
                                        <ImageList sx={{ width: '100%', height: 'auto', mb: 2 }} cols={2} rowHeight={150}>
                                            {images.map((image, index) => (
                                                <ImageListItem key={index} sx={{ position: 'relative' }}>
                                                    <img
                                                        src={image}
                                                        alt={`Tour image ${index + 1}`}
                                                        loading="lazy"
                                                        style={{ height: '150px', objectFit: 'cover' }}
                                                    />
                                                    <IconButton
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 8,
                                                            right: 8,
                                                            bgcolor: 'rgba(0, 0, 0, 0.5)',
                                                            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
                                                        }}
                                                        onClick={() => handleRemoveImage(index)}
                                                    >
                                                        <DeleteIcon sx={{ color: 'white' }} />
                                                    </IconButton>
                                                </ImageListItem>
                                            ))}
                                        </ImageList>
                                    )}
                                </Box>

                                {/* Description Section */}
                                <Box sx={{ mb: 4 }}>
                                    <Typography variant="h6" gutterBottom>
                                        {t('pages.tours.operator.tourForm.description')}
                                    </Typography>
                                    {isEditing ? (
                                        <TextField
                                            value={editedTour.description || ''}
                                            onChange={(e) => setEditedTour({ ...editedTour, description: e.target.value })}
                                            fullWidth
                                            multiline
                                            rows={4}
                                            variant="outlined"
                                        />
                                    ) : (
                                        renderExpandableText(tour.description, 'description')
                                    )}
                                </Box>

                                {/* Meeting Point Section */}
                                <Box sx={{ mb: 4 }}>
                                    <Typography variant="h6" gutterBottom>
                                        {t('pages.tours.operator.tourForm.meetingPoint')}
                                    </Typography>
                                    {isEditing ? (
                                        <TextField
                                            value={editedTour.meetingPoint || ''}
                                            onChange={(e) => setEditedTour({ ...editedTour, meetingPoint: e.target.value })}
                                            fullWidth
                                            variant="outlined"
                                        />
                                    ) : (
                                        renderExpandableText(tour.meetingPoint, 'meetingPoint')
                                    )}
                                </Box>

                                {/* Details Section */}
                                <Box>
                                    <Typography variant="h6" gutterBottom>
                                        {t('pages.tours.operator.tourManagement.included')}
                                    </Typography>
                                    <List dense>
                                        {isEditing ? (
                                            <>
                                                {(editedTour.included || []).map((item, index) => (
                                                    <ListItem key={index}>
                                                        <ListItemIcon>
                                                            <CheckCircleIcon color="success" fontSize="small" />
                                                        </ListItemIcon>
                                                        <TextField
                                                            value={item}
                                                            onChange={(e) => {
                                                                const newIncluded = [...(editedTour.included || [])];
                                                                newIncluded[index] = e.target.value;
                                                                setEditedTour({ ...editedTour, included: newIncluded });
                                                            }}
                                                            fullWidth
                                                            multiline
                                                            variant="outlined"
                                                            size="small"
                                                            sx={{ mr: 1 }}
                                                        />
                                                        <IconButton 
                                                            onClick={() => handleRemoveItem('included', index)}
                                                            size="small"
                                                            sx={{ color: '#d32f2f' }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </ListItem>
                                                ))}
                                                <Button
                                                    startIcon={<AddIcon />}
                                                    onClick={() => handleAddItem('included')}
                                                    sx={{ mt: 1, color: '#316C6E' }}
                                                >
                                                    Add Item
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                {(tour.included || []).map((item, index) => (
                                                    <ListItem key={index}>
                                                        <ListItemIcon>
                                                            <CheckCircleIcon color="success" fontSize="small" />
                                                        </ListItemIcon>
                                                        <ListItemText 
                                                            primary={renderExpandableText(item, 'included', index)}
                                                            sx={{ m: 0 }}
                                                        />
                                                    </ListItem>
                                                ))}
                                                {(!tour.included || tour.included.length === 0) && (
                                                    <ListItem>
                                                        <ListItemText secondary="No items included" />
                                                    </ListItem>
                                                )}
                                            </>
                                        )}
                                    </List>

                                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                                        {t('pages.tours.operator.tourManagement.notIncluded')}
                                    </Typography>
                                    <List dense>
                                        {isEditing ? (
                                            <>
                                                {(editedTour.notIncluded || []).map((item, index) => (
                                                    <ListItem key={index}>
                                                        <ListItemIcon>
                                                            <CancelIcon color="error" fontSize="small" />
                                                        </ListItemIcon>
                                                        <TextField
                                                            value={item}
                                                            onChange={(e) => {
                                                                const newNotIncluded = [...(editedTour.notIncluded || [])];
                                                                newNotIncluded[index] = e.target.value;
                                                                setEditedTour({ ...editedTour, notIncluded: newNotIncluded });
                                                            }}
                                                            fullWidth
                                                            multiline
                                                            variant="outlined"
                                                            size="small"
                                                            sx={{ mr: 1 }}
                                                        />
                                                        <IconButton 
                                                            onClick={() => handleRemoveItem('notIncluded', index)}
                                                            size="small"
                                                            sx={{ color: '#d32f2f' }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </ListItem>
                                                ))}
                                                <Button
                                                    startIcon={<AddIcon />}
                                                    onClick={() => handleAddItem('notIncluded')}
                                                    sx={{ mt: 1, color: '#316C6E' }}
                                                >
                                                    Add Item
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                {(tour.notIncluded || []).map((item, index) => (
                                                    <ListItem key={index}>
                                                        <ListItemIcon>
                                                            <CancelIcon color="error" fontSize="small" />
                                                        </ListItemIcon>
                                                        <ListItemText 
                                                            primary={renderExpandableText(item, 'notIncluded', index)}
                                                            sx={{ m: 0 }}
                                                        />
                                                    </ListItem>
                                                ))}
                                                {(!tour.notIncluded || tour.notIncluded.length === 0) && (
                                                    <ListItem>
                                                        <ListItemText secondary="No items excluded" />
                                                    </ListItem>
                                                )}
                                            </>
                                        )}
                                    </List>

                                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                                        {t('pages.tours.operator.tourManagement.tips')}
                                    </Typography>
                                    <List dense>
                                        {isEditing ? (
                                            <>
                                                {(editedTour.tips || []).map((item, index) => (
                                                    <ListItem key={index}>
                                                        <ListItemIcon>
                                                            <InfoIcon color="info" fontSize="small" />
                                                        </ListItemIcon>
                                                        <TextField
                                                            value={item}
                                                            onChange={(e) => {
                                                                const newTips = [...(editedTour.tips || [])];
                                                                newTips[index] = e.target.value;
                                                                setEditedTour({ ...editedTour, tips: newTips });
                                                            }}
                                                            fullWidth
                                                            multiline
                                                            variant="outlined"
                                                            size="small"
                                                            sx={{ mr: 1 }}
                                                        />
                                                        <IconButton 
                                                            onClick={() => handleRemoveItem('tips', index)}
                                                            size="small"
                                                            sx={{ color: '#d32f2f' }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </ListItem>
                                                ))}
                                                <Button
                                                    startIcon={<AddIcon />}
                                                    onClick={() => handleAddItem('tips')}
                                                    sx={{ mt: 1, color: '#316C6E' }}
                                                >
                                                    Add Item
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                {(tour.tips || []).map((item, index) => (
                                                    <ListItem key={index}>
                                                        <ListItemIcon>
                                                            <InfoIcon color="info" fontSize="small" />
                                                        </ListItemIcon>
                                                        <ListItemText 
                                                            primary={renderExpandableText(item, 'tips', index)}
                                                            sx={{ m: 0 }}
                                                        />
                                                    </ListItem>
                                                ))}
                                                {(!tour.tips || tour.tips.length === 0) && (
                                                    <ListItem>
                                                        <ListItemText secondary="No tips added" />
                                                    </ListItem>
                                                )}
                                            </>
                                        )}
                                    </List>

                                    {/* Save/Cancel Buttons */}
                                    {isEditing && (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, gap: 2 }}>
                                            <Button
                                                variant="contained"
                                                onClick={handleSave}
                                                sx={{ 
                                                    bgcolor: '#316C6E',
                                                    '&:hover': { bgcolor: '#255557' }
                                                }}
                                            >
                                                {t('common.save')}
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                onClick={handleCancel}
                                                sx={{ 
                                                    color: '#666',
                                                    borderColor: '#666',
                                                    '&:hover': { borderColor: '#444' }
                                                }}
                                            >
                                                {t('common.cancel')}
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            </Paper>
                        </Grid>

                        {/* Tour Dates Section */}
                        <Grid item xs={12} md={8}>
                            <Paper sx={{ p: 3, bgcolor: '#fff' }}>
                                <TourDatesManager tourId={tourId} />
                            </Paper>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        </Box>
    );
};

export default TourManagementPage; 